import { Button, HStack, Text, useBreakpointValue, type ResponsiveValue } from '@chakra-ui/react';
import type League from '@/models/league';
import type { leagueMember } from '@/models/user';
import { AdminTag, RankChangeIndicator } from '@/components';
import { leagueRowButtonProps, singleLineEllipsisTextProps } from '@/components/singleLineEllipsis';
import {
  getJoinOrderRank,
  getListPosition,
  isPendingLeagueRank,
  isSettledLeagueRank,
  memberHasList,
} from '@/helpers/leagues/leagueMemberSync';
import { ordinalSuffix } from '@/helpers';

export function LeagueListRow(props: {
  league: League;
  memberData: leagueMember;
  isAdmin: boolean;
  isSelected?: boolean;
  tournamentStarted?: boolean;
  onSelect?: () => void;
}) {
  const { league, memberData, isAdmin, isSelected, tournamentStarted = true, onSelect } = props;
  const hasList = memberHasList(memberData);
  const rankToDisplay = isSettledLeagueRank(memberData.currentRank)
    ? memberData.currentRank
    : getJoinOrderRank(league, memberData, memberData.userID);
  const leagueButtonTextSize = useBreakpointValue<ResponsiveValue<number>>({
    base: 12,
    lg: 13,
    xl: 13,
  });

  return (
    <Button
      {...leagueRowButtonProps}
      variant='outline'
      pointerEvents={onSelect ? undefined : 'none'}
      cursor={onSelect ? 'pointer' : 'default'}
      borderWidth='2px'
      borderColor={isSelected ? 'green.500' : undefined}
      onClick={onSelect}
      _hover={onSelect ? undefined : { bg: undefined }}
    >
      <HStack width='full' spacing={4} align='center' minW={0} flex={1}>
        <Text
          {...singleLineEllipsisTextProps}
          flex={1}
          textAlign='start'
          fontSize={leagueButtonTextSize}
        >
          {league.name}
        </Text>
        <HStack spacing={2} flexShrink={0}>
          {isAdmin ? <AdminTag fontSize={leagueButtonTextSize} compact /> : null}
          {!hasList ? (
            <Text fontWeight='bold' fontSize={leagueButtonTextSize}>
              -----
            </Text>
          ) : isPendingLeagueRank(memberData.currentRank) ? (
            <Text fontWeight='bold' fontStyle='italic' fontSize={leagueButtonTextSize}>
              pending
            </Text>
          ) : (
            <>
              <Text fontWeight='bold' fontSize={leagueButtonTextSize}>
                {!tournamentStarted ? (
                  '-----'
                ) : (
                  <>
                    {rankToDisplay}
                    <sup>{ordinalSuffix(rankToDisplay)}</sup>
                  </>
                )}
              </Text>
              {isSettledLeagueRank(memberData.currentRank) && tournamentStarted ? (
                <RankChangeIndicator
                  memberData={memberData}
                  fontSize={leagueButtonTextSize}
                  getListPosition={() => getListPosition(league, memberData, memberData.userID)}
                />
              ) : null}
            </>
          )}
        </HStack>
      </HStack>
    </Button>
  );
}
